"use client";

import type { CSSProperties } from "react";

function SkeletonBar({
  className,
  style,
}: {
  className: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`rounded bg-contrast/6 shimmer shimmer-bg text-foreground ${className}`}
      style={style}
    />
  );
}

export function SearchHeaderSkeleton() {
  return (
    <div className="flex w-full shrink-0 flex-col gap-3 border-b border-solid border-border bg-surface-header px-4 py-4 md:flex-row md:items-center md:justify-between md:px-8 md:py-6">
      <div className="flex items-center gap-3 md:gap-5">
        <SkeletonBar className="size-11 !rounded-full shrink-0 md:hidden" />
        <SkeletonBar className="hidden size-[90px] !rounded-full shrink-0 md:block" />

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <SkeletonBar className="h-4 w-[160px]" />
            <div className="flex items-center gap-2.5">
              <SkeletonBar className="size-3.5 !rounded-full" />
              <SkeletonBar className="size-3.5 !rounded-full" />
            </div>
            <div className="ml-auto md:hidden">
              <SkeletonBar className="size-7 !rounded-[6px]" />
            </div>
          </div>
          <SkeletonBar className="h-[13px] w-40" />
          <div className="flex items-center gap-1.5">
            <SkeletonBar className="size-4 !rounded" />
            <SkeletonBar className="h-[13px] w-[200px]" />
          </div>
          <div className="flex items-center gap-1.5">
            <SkeletonBar className="size-4 !rounded" />
            <SkeletonBar className="h-[13px] w-60" />
          </div>
        </div>
      </div>

      <div className="hidden md:flex shrink-0 flex-col items-end justify-center gap-3">
        <div className="flex items-start gap-3">
          <SkeletonBar className="h-[30px] w-[110px] !rounded-md" />
          <SkeletonBar className="h-[30px] w-[146px] !rounded-md" />
        </div>
        <div className="flex items-start justify-end gap-3">
          <SkeletonBar className="h-[30px] w-[84px] !rounded-md" />
          <SkeletonBar className="h-[30px] w-[110px] !rounded-md" />
        </div>
      </div>
    </div>
  );
}

export function PipelineHeaderSkeleton() {
  return (
    <div className="flex w-full shrink-0 flex-col gap-3 border-b border-solid border-border bg-surface-header px-4 py-4 md:flex-row md:items-center md:justify-between md:px-8 md:py-6">
      <div className="flex items-center gap-3 md:gap-5">
        {/* Mobile avatar 44px */}
        <SkeletonBar className="size-11 !rounded-full shrink-0 md:hidden" />
        {/* Desktop avatar 80px */}
        <SkeletonBar className="hidden size-20 !rounded-full shrink-0 md:block" />

        <div className="flex min-w-0 flex-1 flex-col gap-1.5 md:gap-3">
          {/* Name + socials + stage + mobile menu */}
          <div className="flex flex-wrap items-center gap-2">
            <SkeletonBar className="h-5 w-[140px] md:w-[160px]" />
            <div className="flex items-center gap-2.5">
              <SkeletonBar className="size-3.5 !rounded-full" />
              <SkeletonBar className="size-3.5 !rounded-full" />
            </div>
            <SkeletonBar className="h-[26px] w-[72px] !rounded-[6px]" />
            <div className="ml-auto md:hidden">
              <SkeletonBar className="size-7 !rounded-[6px]" />
            </div>
          </div>
          {/* Meta row */}
          <div className="flex flex-wrap items-start gap-3 md:gap-6">
            <div className="flex flex-col gap-1">
              <SkeletonBar className="h-[13px] w-[80px]" />
              <SkeletonBar className="h-3.5 w-[100px]" />
            </div>
            <div className="flex flex-col gap-1">
              <SkeletonBar className="h-[13px] w-[52px]" />
              <SkeletonBar className="h-3.5 w-[90px]" />
            </div>
            <div className="flex flex-col gap-1">
              <SkeletonBar className="h-[13px] w-[90px]" />
              <SkeletonBar className="h-3.5 w-[110px]" />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop right actions */}
      <div className="hidden items-center gap-3 shrink-0 md:flex">
        <SkeletonBar className="size-7 !rounded-[6px]" />
        <div className="w-px h-[25px] bg-contrast/10" />
        <div className="flex items-center">
          <SkeletonBar className="h-7 w-[140px] !rounded-l-[6px] !rounded-r-none" />
          <SkeletonBar className="h-7 w-[28px] !rounded-l-none !rounded-r-[6px]" />
        </div>
      </div>
    </div>
  );
}
